import type { paths } from '@/api/schema'

// The address form's small grammar (design 170): the postal code's shape, and
// the ONE spelling of a stored address line — « 1548, avenue de la Montagne
// Est » — split for the edit forms and joined at the save.

type GeocodeMeta =
  paths['/api/service_locations/geocode']['post']['responses']['200']['content']['application/vnd.api+json']['meta']
// A house as the lookup answers it: the point, and the address as the registry spells it.
export type House = GeocodeMeta['houses'][number]

// What the form holds: the number as the office typed it, the postal code, and
// the street + town for the caller without a postal code.
export interface AddressDraft {
  civicNumber: string
  postalCode: string
  street: string
  city: string
}

export const blankAddressDraft = (): AddressDraft => ({
  civicNumber: '',
  postalCode: '',
  street: '',
  city: '',
})

const POSTAL_CODE = /^[A-Z]\d[A-Z]\d[A-Z]\d$/

// « g6v 1a1 » → « G6V1A1 »: what the registry stores and the lookup sends.
export const compactPostalCode = (raw: string | null | undefined): string =>
  (raw ?? '').replace(/\s+/g, '').toUpperCase()

// Six characters in the Canadian shape: the moment the lookup may fire.
export const isCompletePostalCode = (raw: string | null | undefined): boolean =>
  POSTAL_CODE.test(compactPostalCode(raw))

// « G6V1A1 » → « G6V 1A1 » for display; anything else is returned as typed.
export const formatPostalCode = (raw: string | null | undefined): string => {
  const compact = compactPostalCode(raw)
  return compact.length === 6 ? `${compact.slice(0, 3)} ${compact.slice(3)}` : (raw ?? '').trim()
}

// The stored line: the number as the office typed it (« 1548B », « 301-1234 »
// — the registry has no apartments), then the street as the registry spells it.
export const joinAddressLine = (civicNumber: string | null | undefined, street: string | null | undefined): string => {
  const number = (civicNumber ?? '').trim()
  const name = (street ?? '').trim()
  if (!number) return name
  if (!name) return number
  return `${number}, ${name}`
}

// The head of a line that opens with a number: digits, an optional unit-civic
// pair (« 4-130 »), an optional unit letter that is not the start of a word
// (« 5032 J », « 1548B » — never « 13715rue »), then an optional comma.
const LINE_HEAD = /^(\d+(?:\s*-\s*\d+)?(?:\s*[A-Za-z](?![A-Za-z.]))?)\s*,?\s*([\s\S]*)$/

// The edit forms' initial values, from a line stored in any of the spellings
// the app has had: « 1548, avenue… », « 1548 av de la montagne », « 4-130, rue
// Wolfe », or a line with no number at all (number '', street = the line).
export const splitAddressLine = (line: string | null | undefined): { civicNumber: string; street: string } => {
  const trimmed = (line ?? '').trim()
  const match = trimmed.match(LINE_HEAD)
  if (!match) return { civicNumber: '', street: trimmed }
  return { civicNumber: match[1].replace(/\s+/g, ''), street: match[2].trim() }
}

// The address a save stores: the registry's street, town and postal code when a
// house answered (his call: one address, one spelling), the typed text
// otherwise. The number is always the person's — the registry has no apartments.
export const resolvedAddress = (draft: AddressDraft, house?: House | null): AddressDraft => ({
  civicNumber: draft.civicNumber.trim(),
  street: (house?.street_label ?? draft.street).trim(),
  city: (house?.city ?? draft.city).trim(),
  postalCode: formatPostalCode(house?.postal_code ?? draft.postalCode),
})

// An address kept as ONE line (the org HQ, a depot): « 1548, avenue de la
// Montagne Est, Lévis, G6V 1A1 ». Mirrored by Geocoding::OneLineAddress.
export const formatOneLineAddress = (draft: AddressDraft): string =>
  [joinAddressLine(draft.civicNumber, draft.street), draft.city.trim(), formatPostalCode(draft.postalCode)]
    .filter(Boolean)
    .join(', ')

const PROVINCE = /^(?:[A-Z]{2}|Canada)$/i
const TRAILING_POSTAL = /\s+([A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d)$/

// The one line back into the row — our own spelling, or what was typed before
// it: a trailing province, a postal code at the end (alone or glued to the
// town), the town before it, the rest the civic line.
export const parseOneLineAddress = (line: string | null | undefined): AddressDraft => {
  const tokens = (line ?? '').split(',').map((t) => t.trim()).filter(Boolean)
  if (tokens.length === 0) return blankAddressDraft()

  if (tokens.length > 1 && PROVINCE.test(tokens[tokens.length - 1])) tokens.pop()
  let postalCode = ''
  if (tokens.length > 1 && isCompletePostalCode(tokens[tokens.length - 1])) {
    postalCode = tokens.pop() ?? ''
  } else if (tokens.length > 1) {
    const glued = tokens[tokens.length - 1].match(TRAILING_POSTAL)
    if (glued) {
      postalCode = glued[1]
      tokens[tokens.length - 1] = tokens[tokens.length - 1].replace(TRAILING_POSTAL, '').trim()
    }
  }
  const city = tokens.length > 1 ? (tokens.pop() ?? '') : ''
  const { civicNumber, street } = splitAddressLine(tokens.join(', '))
  return { civicNumber, street, city, postalCode: formatPostalCode(postalCode) }
}
